import React, { useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import cn from '@/utils/cn';
import SPAButton from '../shared/SPAButton';
import SPADataTable, { dataFrameClass } from '../shared/SPADataTable';
import SPASectionHeading from '../shared/SPASectionHeading';
import { DailyPoint } from './types';
import {
  ChartRow, foldUseCases, formatTokens, SERIES_DARK, SERIES_LIGHT,
} from './usagePalette';

const columnHelper = createColumnHelper<ChartRow>();

const isDark = () => {
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr) return attr === 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
};

const UsageTable = ({ rows, series }: { rows: ChartRow[]; series: string[] }) => {
  const columns = [
    columnHelper.accessor('date', { header: 'Date' }),
    ...series.map(name => columnHelper.accessor(row => (row[name] as number) ?? 0, {
      cell: info => formatTokens(info.getValue()),
      header: name,
      id: name,
      meta: { align: 'right' },
    })),
  ] as ColumnDef<ChartRow, unknown>[];

  return <SPADataTable columns={columns} data={rows} defaultSort={[]} />;
};

const CHART_FRAME = cn(dataFrameClass, 'tw-box-border tw-h-72 tw-p-4');

const UsageChart = ({ daily }: { daily?: DailyPoint[] }) => {
  const [asTable, setAsTable] = useState(false);
  const { rows, series } = foldUseCases(daily ?? []);
  const colors = isDark() ? SERIES_DARK : SERIES_LIGHT;

  return (
    <section>
      <SPASectionHeading
        meta={rows.length > 0 && (
          <SPAButton size="xs" variant="secondary" onClick={() => setAsTable(!asTable)}>
            {asTable ? 'Show chart' : 'Show table'}
          </SPAButton>
        )}
        title="Tokens per day"
      />
      {!daily && (
        <div aria-hidden="true" className={cn(CHART_FRAME, 'tw-animate-pulse tw-bg-spa-bg-skeleton-subtle')} />
      )}
      {daily && rows.length === 0 && (
        <div className={cn(dataFrameClass, 'tw-py-12 tw-text-center tw-text-sm tw-text-spa-tertiary')}>
          No AI calls recorded in this window.
        </div>
      )}
      {rows.length > 0 && asTable && <UsageTable rows={rows} series={series} />}
      {rows.length > 0 && !asTable && (
        <div className={CHART_FRAME}>
          <ResponsiveContainer height="100%" width="100%">
            <BarChart data={rows} margin={{ bottom: 0, left: 0, right: 8, top: 8 }}>
              <CartesianGrid stroke="var(--spa-border)" vertical={false} />
              <XAxis
                axisLine={{ stroke: 'var(--spa-border)' }}
                dataKey="date"
                tick={{ fill: 'var(--spa-text-secondary)', fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tick={{ fill: 'var(--spa-text-secondary)', fontSize: 11 }}
                tickFormatter={formatTokens}
                tickLine={false}
                width={44}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--spa-bg-secondary)',
                  border: '1px solid var(--spa-border)',
                  borderRadius: 6,
                  color: 'var(--spa-text-primary)',
                  fontSize: 12,
                }}
                cursor={{ fill: 'var(--spa-bg-tertiary)' }}
                formatter={(value: number) => formatTokens(value)}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              {series.map((name, i) => (
                <Bar
                  // 2px surface gap between stacked segments, per the mark spec.
                  key={name}
                  dataKey={name}
                  fill={colors[i % colors.length]}
                  radius={i === series.length - 1 ? [3, 3, 0, 0] : undefined}
                  stackId="tokens"
                  stroke="var(--spa-bg-secondary)"
                  strokeWidth={1}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
};

export default UsageChart;
